<template>
  <div class="chat-page">
    <div class="intro-panel">
      <div class="intro-inner">
        <span class="intro-badge">給 NPO 夥伴的專屬邀請</span>
        <p class="intro-text">
          專注打造物資「無償共享」生態圈，運用科技的力量，讓社福團體募集物資更輕鬆、更有效率！
        </p>
        <div class="video-wrap">
          <iframe
            class="video-frame"
            src="https://www.youtube.com/embed/DLnjEWWZycs"
            title="GC 贈物網影片"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
          />
        </div>
        <p class="video-tip">↑ 看看其他公益夥伴如何在 GC 贈物網獲得支持</p>
      </div>
    </div>

    <div class="chat-panel">
      <div class="chat-header">
        <div class="agent-meta">
          <div class="agent-avatar">👩‍🏫</div>
          <div>
            <h2 class="agent-name">GC贈物網 公益小編</h2>
            <p class="agent-status">● 在線協助</p>
          </div>
        </div>
        <button class="qa-btn" @click="toggleSidebar">📖 更多 QA</button>
      </div>

      <div ref="chatBox" class="chat-box">
        <div
          v-for="(msg, index) in chatMessages"
          :key="`${msg.side}-${index}`"
          class="msg-row"
          :class="msg.side === 'right' ? 'msg-row-right' : 'msg-row-left'"
        >
          <div
            class="msg-bubble"
            :class="msg.side === 'right' ? 'msg-right' : 'msg-left'"
            v-html="msg.text"
            @click="handleMessageClick"
          />
        </div>
        <div v-if="typing" class="msg-row msg-row-left">
          <div class="typing-bubble">
            <span class="typing-dot" />
            <span class="typing-dot" />
            <span class="typing-dot" />
          </div>
        </div>
      </div>

      <div class="action-bar">
        <button
          v-for="(opt, index) in actionOptions"
          :key="`${opt.text}-${index}`"
          class="action-btn"
          :class="{ 'action-btn-primary': opt.primary }"
          @click="onActionClick(opt)"
        >
          {{ opt.text }}
        </button>
      </div>
    </div>

    <div
      v-if="sidebarOpen"
      class="sidebar-mask"
      @click="toggleSidebar"
    />
    <aside class="sidebar" :class="{ 'sidebar-open': sidebarOpen }">
      <div class="sidebar-header">
        <h3>更多 QA (20題)</h3>
        <button class="close-btn" @click="toggleSidebar">✕</button>
      </div>

      <div class="sidebar-tools">
        <input
          v-model.trim="faqKeyword"
          type="text"
          placeholder="輸入關鍵字（如：收據、運費）..."
          class="search-input"
        >
        <div class="chips">
          <button
            v-for="cat in categories"
            :key="cat"
            class="chip-btn"
            :class="{ 'chip-active': currentCategory === cat }"
            @click="setCategory(cat)"
          >
            {{ cat }}
          </button>
        </div>
      </div>

      <div class="faq-list">
        <details
          v-for="faq in filteredFaqs"
          :key="faq.id"
          class="faq-item"
          :open="openedFaqIds.includes(faq.id)"
          @toggle="onFaqToggle(faq.id, $event)"
        >
          <summary>
            <span>Q: {{ faq.q }}</span>
            <span class="arrow">▼</span>
          </summary>
          <p v-html="faq.a" />
        </details>
      </div>
    </aside>
  </div>
</template>

<script>
const FAQ_DB = [
  { id: 1, cat: '帳號資格', q: '如何註冊帳號？', a: '點選「登入/註冊」。輸入手機號碼並通過簡訊驗證後，至「編輯資料」填寫真實姓名與收件資訊（重要：須與證件相符，以免無法領貨）。' },
  { id: 2, cat: '帳號資格', q: '申請公益帳號需要準備哪些文件？', a: '點選變更「會員身份」，並準備政府立案證明及相關資料。上傳後 7-10 個工作日，會有專人為您審核撥電。' },
  { id: 3, cat: '帳號資格', q: '公益帳號與一般帳號差在哪？', a: '公益帳號刊登需求有首頁露出版位、專屬的「感謝模板」功能，能讓募集效率更高，快速建立信任感。' },
  { id: 4, cat: '帳號資格', q: '單位聯繫人異動怎麼辦？', a: '點選「交接預告」，將有專人聯繫。或來信告知（<a href="mailto:shin@give-circle.com">shin@give-circle.com</a>）。' },
  { id: 5, cat: '帳號資格', q: '同一個單位，有分會，還能申請帳號嗎？', a: '可以，仍需特別申請。手機號碼不可重複註冊。' },
  { id: 6, cat: '徵求物資', q: '如何寫需求才會有人送？', a: '秘訣是「規格與用途寫清楚」。例如標明「抽取式衛生紙」而非只寫衛生紙，並說明是給獨居長輩使用，這能幫助贈送者精準評估。' },
  { id: 7, cat: '徵求物資', q: '有什麼東西是禁止募集的？', a: '法令禁止網購寄送的醫療處方藥品。詳見規範：<a href="https://www.give-circle.com/story/22076" target="_blank">什麼東西不能送？</a>' },
  { id: 8, cat: '徵求物資', q: '徵求數量達標後，怎麼提前結束？', a: '平台數量達標會自動下架，若想提前結束，可進入「需求管理」後台介面，手動關閉或下架。' },
  { id: 9, cat: '徵求物資', q: '可以指定要全新或特定效期的物資嗎？', a: '可以。建議在需求描述中明確標註（例如「僅收全新」、「效期需大於三個月」）。' },
  { id: 10, cat: '徵求物資', q: '大型家具適合在平台上募集嗎？', a: '超商與 i 郵箱皆有尺寸限制。大型物資建議在描述中註明，並透過站內信與贈送者約定「自取」，或找新竹物流等大型宅配。' },
  { id: 11, cat: '徵求物資', q: '運費無法負擔怎麼辦？', a: '在需求說明頁寫上無法負擔運費請包涵，並勾選由「贈送者」支付運费。贈送者進行捐贈時就會知道。' },
  { id: 12, cat: '徵求物資', q: '有哪些寄件方式可選？', a: '平台串接了：郵局 i 郵箱、全家店到店、萊爾富店到店以及新竹物流。若有其他物流，可以勾選「其他物流」，但此方式需自行追蹤貨態。' },
  { id: 13, cat: '其他', q: '收到壞掉或不符需求的產品怎麼辦？', a: '建議先透過站內私訊了解是否為誤會。若遇惡意寄件，請直接使用「好壞名單」將對方列入黑名單，並通知客服。<a href="mailto:service@give-circle.com">service@give-circle.com</a>' },
  { id: 14, cat: '徵求物資', q: '可以合併收取同一人的多項物資嗎？', a: '可以（手機限定功能）。若同一位贈送者有多個物品要寄給您，可透過手機版操作「合併寄送」，節省運費與時間。' },
  { id: 15, cat: '其他', q: '如何開立捐贈收據？', a: '確認捐物收到後，除了到頁面留言感謝，其餘照單位運作方式。平台貼心設定「感謝模板」，讓您輕鬆留下感謝。' },
  { id: 16, cat: '其他', q: '用不到的物資可以轉贈嗎？', a: '可以。若暫無需求，建議與原贈送者打聲招呼後，將物資作為「禮物」重新發布回禮物池或轉贈，讓愛心繼續流動。' },
  { id: 17, cat: '其他', q: '如何在平台上表達感謝？', a: '平台特別開發了「感謝模板」功能，您可以善用此模板，一鍵發送溫馨的感謝留言給捐物者。' },
  { id: 18, cat: '其他', q: '需要回報物資使用狀況嗎？', a: '我們非常鼓勵！您可以透過「<a href="https://www.give-circle.com/story/22339" target="_blank">一鍵 tag 的功能</a>」發布圖文說說，這能大幅提升機構透明度與捐贈者的信任感。' },
  { id: 19, cat: '其他', q: '企業想進行大宗捐贈怎麼對接？', a: '若有大宗專案需求，企業通常會透過首頁「企業合作」專區或信箱 <a href="mailto:service@give-circle.com">service@give-circle.com</a> 聯繫，由官方協助媒合。' },
  { id: 20, cat: '其他', q: '公益團體可以發起小額募款嗎？', a: 'GC 贈物網以「募集實體物資」為主要使用目的。' }
]

const STORY = {
  start: {
    msgs: [
      { side: 'left', text: '哈囉夥伴！我是 GC 贈物網公益小編。辛苦了！是不是常為了找物資預算而發愁？' },
      { side: 'left', text: '聽說你們想加入，但怕流程太複雜對吧？想先了解哪部分呢？' }
    ],
    options: [
      { text: '費用怎麼算？', next: 'cost' },
      { text: '註冊會很麻煩嗎？', next: 'docs' }
    ]
  },
  cost: {
    msgs: [
      { side: 'right', text: '說實話，真的不用錢嗎？' },
      { side: 'left', text: '<b>真的不用錢！</b>我們一毛都不收，資源就留給需要個案！連簡訊驗證費用都由我們吸收喔～<br><span class="inline-link" data-faq-id="1">[如何註冊]</span>' }
    ],
    options: [
      { text: '那怎麼募物資？', next: 'get' },
      { text: '註冊會很麻烦嗎？', next: 'docs' }
    ]
  },
  docs: {
    msgs: [
      { side: 'right', text: '申請會不會很麻煩？要一堆文件？' },
      { side: 'left', text: '超簡單！註冊會是一般會員身份，之後上傳「政府立案證明」填寫相關資料，公益部將於 <b>7-10 個工作天</b> 與您聯繫。' },
      { side: 'left', text: '在這之前，仍可以用一般會員身份在平台上操作喔！<br><span class="inline-link" data-faq-id="2">[查看應備文件]</span>' }
    ],
    options: [
      { text: '那怎麼募物資？', next: 'get' },
      { text: '運費誰出？', next: 'shipping' }
    ]
  },
  get: {
    msgs: [
      { side: 'right', text: '我想知道怎麼要物資比較快？' },
      { side: 'left', text: '徵求物資的秘訣是：<b>需求原因、需求規格寫清楚</b>。比如：服務經濟弱勢個案，需要抽取式衛生紙 10 串，送的人才不會給錯。' },
      { side: 'left', text: '而且能設定募集數量上限，也不怕超量！<br><span class="inline-link" data-faq-id="6">[看募集技巧]</span>' }
    ],
    options: [{ text: '最怕運費很貴...', next: 'shipping' }]
  },
  shipping: {
    msgs: [
      { side: 'right', text: '最怕運費很貴...' },
      { side: 'left', text: '別擔心，可以設定<b>「贈送者負擔」</b>。民眾透過郵局 i 郵箱、萊爾富寄送都很方便，還有<b>環保集點綠點回饋</b>捐贈者。' },
      { side: 'left', text: '至於大型家具可以約自取。<br><span class="inline-link" data-faq-id="11">[查看物流規範]</span>' }
    ],
    options: [{ text: '收據怎麼開？', next: 'receipt' }]
  },
  receipt: {
    msgs: [
      { side: 'right', text: '收到東西後，收據怎麼處理？' },
      { side: 'left', text: '確認捐物收到後，除了到頁面留言感謝，其餘照單位運作方式。' },
      { side: 'left', text: '平台貼心設定「感謝模板」，讓您輕鬆留下感謝！<br><span class="inline-link" data-faq-id="15">[查看收據流程]</span>' }
    ],
    options: [{ text: '🚀 我準備好了，立即註冊！', url: 'https://www.give-circle.com/', primary: true }]
  }
}

export default {
  name: 'ChatPage',
  data () {
    return {
      categories: ['全部', '帳號資格', '徵求物資', '其他'],
      faqDb: FAQ_DB,
      chatMessages: [],
      actionOptions: [],
      typing: false,
      sidebarOpen: false,
      currentCategory: '全部',
      faqKeyword: '',
      openedFaqIds: []
    }
  },
  computed: {
    filteredFaqs () {
      const kw = this.faqKeyword.toLowerCase()
      return this.faqDb.filter((faq) => {
        const hitCategory = this.currentCategory === '全部' || faq.cat === this.currentCategory
        const hitKeyword = faq.q.toLowerCase().includes(kw) || faq.a.toLowerCase().includes(kw)
        return hitCategory && hitKeyword
      })
    }
  },
  mounted () {
    this.playStory('start')
  },
  methods: {
    sleep (ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },
    scrollToBottom () {
      this.$nextTick(() => {
        if (this.$refs.chatBox) {
          this.$refs.chatBox.scrollTo({ top: this.$refs.chatBox.scrollHeight, behavior: 'smooth' })
        }
      })
    },
    async playStory (nodeKey) {
      const node = STORY[nodeKey]
      if (!node) return
      this.actionOptions = []
      for (const msg of node.msgs) {
        this.typing = true
        this.scrollToBottom()
        await this.sleep(900)
        this.typing = false
        this.chatMessages.push(msg)
        this.scrollToBottom()
        await this.sleep(450)
      }
      this.actionOptions = node.options
      this.scrollToBottom()
    },
    onActionClick (opt) {
      if (opt.url) {
        window.open(opt.url, '_blank')
        return
      }
      if (opt.next) {
        this.playStory(opt.next)
      }
    },
    handleMessageClick (event) {
      const link = event.target.closest('[data-faq-id]')
      if (!link) return
      const faqId = Number(link.dataset.faqId)
      this.openSidebar(faqId)
    },
    toggleSidebar () {
      this.sidebarOpen = !this.sidebarOpen
    },
    openSidebar (faqId) {
      this.sidebarOpen = true
      if (!faqId) return
      const item = this.faqDb.find((faq) => faq.id === faqId)
      if (!item) return
      this.currentCategory = '全部'
      this.faqKeyword = item.q.slice(0, 4)
      this.openedFaqIds = [faqId]
    },
    setCategory (cat) {
      this.currentCategory = cat
      this.faqKeyword = ''
      this.openedFaqIds = []
    },
    onFaqToggle (id, event) {
      if (event.target.open) {
        if (!this.openedFaqIds.includes(id)) {
          this.openedFaqIds.push(id)
        }
      } else {
        this.openedFaqIds = this.openedFaqIds.filter((faqId) => faqId !== id)
      }
    }
  }
}
</script>

<style scoped>
.chat-page { display: flex; height: 100vh; background: #f8fafc; color: #1f2937; overflow: hidden; }
.intro-panel { width: 55%; padding: 48px; background: #fff; border-right: 1px solid #e5e7eb; overflow-y: auto; }
.intro-inner { max-width: 640px; margin: 0 auto; }
.intro-badge { display: inline-block; padding: 6px 12px; border-radius: 999px; background: #ffedd5; color: #ea580c; font-size: 12px; font-weight: 700; margin-bottom: 20px; }
.intro-text { color: #64748b; line-height: 1.7; margin-bottom: 20px; }
.video-wrap { border-radius: 24px; overflow: hidden; background: #0f172a; box-shadow: 0 10px 25px rgba(15, 23, 42, 0.15); aspect-ratio: 16 / 9; }
.video-frame { width: 100%; height: 100%; display: block; border: 0; }
.video-tip { margin-top: 12px; color: #94a3b8; font-size: 12px; text-align: center; }

.chat-panel { width: 45%; min-width: 340px; height: 100vh; position: relative; display: flex; flex-direction: column; background: #f8fafc; overflow: hidden; }
.chat-header { position: sticky; top: 0; z-index: 10; display: flex; justify-content: space-between; align-items: center; padding: 14px 16px; background: rgba(255, 255, 255, 0.95); border-bottom: 1px solid #e5e7eb; backdrop-filter: blur(8px); flex-shrink: 0; }
.agent-meta { display: flex; align-items: center; gap: 10px; }
.agent-avatar { width: 40px; height: 40px; border-radius: 999px; background: #f97316; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 20px; }
.agent-name { margin: 0; font-size: 14px; }
.agent-status { margin: 2px 0 0; font-size: 11px; color: #16a34a; font-weight: 700; }
.qa-btn { border: 1px solid #e2e8f0; background: #fff; color: #475569; border-radius: 8px; font-size: 12px; font-weight: 700; padding: 6px 10px; cursor: pointer; transition: background 0.2s; }
.qa-btn:active { background: #f1f5f9; }

.chat-box { flex: 1; overflow-y: auto; padding: 20px 16px 120px; -webkit-overflow-scrolling: touch; }
.msg-row { display: flex; margin-bottom: 14px; }
.msg-row-left { justify-content: flex-start; }
.msg-row-right { justify-content: flex-end; }
.msg-bubble { max-width: 86%; border-radius: 18px; padding: 12px 14px; line-height: 1.6; font-size: 14px; word-break: break-word; }
.msg-left { background: #fff; color: #334155; border: 1px solid #e2e8f0; border-bottom-left-radius: 4px; }
.msg-right { background: #f97316; color: #fff; border-bottom-right-radius: 4px; }
.typing-bubble { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #e2e8f0; border-radius: 18px; border-bottom-left-radius: 4px; padding: 12px 14px; }
.typing-dot { width: 6px; height: 6px; border-radius: 50%; background: #f97316; animation: blink 1.2s infinite; }
.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes blink { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }

.action-bar { position: absolute; left: 0; right: 0; bottom: 0; z-index: 20; display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 8px; padding: 24px 16px 16px; background: linear-gradient(to top, #f8fafc 60%, rgba(248, 250, 252, 0)); }
.action-btn { border: 1px solid #fed7aa; background: #fff; color: #ea580c; border-radius: 999px; padding: 10px 16px; font-size: 14px; font-weight: 700; cursor: pointer; transition: transform 0.1s, background 0.2s; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
.action-btn:active { transform: scale(0.95); background: #fff7ed; }
.action-btn-primary { background: #f97316; border-color: #f97316; color: #fff; }
.action-btn-primary:active { background: #ea580c; }

.sidebar-mask { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.28); z-index: 40; }
.sidebar { position: fixed; right: 0; top: 0; width: min(420px, 92vw); height: 100vh; background: #fff; transform: translateX(100%); transition: transform 0.3s ease; z-index: 50; display: flex; flex-direction: column; box-shadow: -12px 0 30px rgba(15, 23, 42, 0.2); }
.sidebar-open { transform: translateX(0); }
.sidebar-header { display: flex; justify-content: space-between; align-items: center; padding: 18px; border-bottom: 1px solid #e5e7eb; }
.sidebar-header h3 { margin: 0; font-size: 18px; }
.close-btn { width: 30px; height: 30px; border-radius: 999px; border: none; background: #f1f5f9; cursor: pointer; transition: background 0.2s; -webkit-tap-highlight-color: transparent; }
.close-btn:active { background: #e2e8f0; }
.sidebar-tools { padding: 14px; border-bottom: 1px solid #e5e7eb; background: #f8fafc; }
.search-input { width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 12px; margin-bottom: 10px; font-size: 16px; }
.chips { display: flex; gap: 8px; overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 4px; }
.chips::-webkit-scrollbar { display: none; }
.chip-btn { white-space: nowrap; border: 1px solid #cbd5e1; background: #fff; color: #475569; border-radius: 999px; padding: 6px 10px; font-size: 12px; font-weight: 700; cursor: pointer; transition: background 0.2s; -webkit-tap-highlight-color: transparent; }
.chip-btn:active { background: #f1f5f9; }
.chip-active { background: #f97316; color: #fff; border-color: #f97316; }
.faq-list { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; }
.faq-item { border-bottom: 1px solid #f1f5f9; padding: 14px; }
.faq-item summary { list-style: none; display: flex; justify-content: space-between; align-items: center; cursor: pointer; font-weight: 700; font-size: 14px; color: #334155; padding: 4px 0; -webkit-tap-highlight-color: transparent; }
.faq-item .arrow { color: #fb923c; transition: transform 0.2s ease; margin-left: 12px; font-size: 12px; }
.faq-item[open] .arrow { transform: rotate(180deg); }
.faq-item p { margin: 10px 0 0; border-left: 2px solid #fed7aa; padding-left: 10px; font-size: 12px; line-height: 1.7; color: #64748b; }

:deep(.inline-link) { color: #f97316; font-weight: 700; cursor: pointer; text-decoration: underline; -webkit-tap-highlight-color: transparent; }
:deep(a) { color: #f97316; text-decoration: underline; -webkit-tap-highlight-color: transparent; }

/* 平板设备适配 (768px - 980px) */
@media (max-width: 980px) {
  .chat-page { flex-direction: column; }
  .intro-panel { width: 100%; min-width: 0; padding: 32px 24px; }
  .chat-panel { width: 100%; min-width: 0; height: 70vh; min-height: 500px; }
}

/* 移动端适配 (< 768px) */
@media (max-width: 768px) {
  .intro-panel { padding: 24px 16px; }
  .intro-badge { font-size: 11px; padding: 5px 10px; margin-bottom: 16px; }
  .intro-text { font-size: 14px; line-height: 1.6; margin-bottom: 16px; }
  .video-wrap { border-radius: 16px; }
  .video-tip { font-size: 11px; margin-top: 8px; }
  
  .chat-panel { height: 65vh; min-height: 450px; }
  .chat-header { padding: 12px 12px; }
  .agent-avatar { width: 36px; height: 36px; font-size: 18px; }
  .agent-name { font-size: 13px; }
  .agent-status { font-size: 10px; }
  .qa-btn { font-size: 11px; padding: 6px 8px; }
  
  .chat-box { padding: 16px 12px 100px; }
  .msg-bubble { max-width: 82%; font-size: 13px; padding: 10px 12px; }
  
  .action-bar { padding: 20px 12px 12px; gap: 6px; }
  .action-btn { font-size: 13px; padding: 9px 14px; min-height: 44px; }
  
  .sidebar { width: 100%; max-width: 100%; }
  .sidebar-header { padding: 16px; }
  .sidebar-header h3 { font-size: 16px; }
  .close-btn { width: 32px; height: 32px; font-size: 18px; }
  .sidebar-tools { padding: 12px; }
  .search-input { padding: 12px; font-size: 16px; }
  .chips { gap: 6px; padding-bottom: 2px; }
  .chip-btn { padding: 7px 12px; font-size: 12px; }
  .faq-item { padding: 12px; }
  .faq-item summary { font-size: 13px; padding: 6px 0; }
  .faq-item p { font-size: 12px; margin-top: 8px; }
}

/* 小屏幕移动端适配 (< 480px) */
@media (max-width: 480px) {
  .intro-panel { padding: 20px 12px; }
  .intro-badge { font-size: 10px; padding: 4px 8px; margin-bottom: 12px; }
  .intro-text { font-size: 13px; line-height: 1.5; margin-bottom: 12px; }
  .video-wrap { border-radius: 12px; }
  .video-tip { font-size: 10px; }
  
  .chat-panel { height: 60vh; min-height: 400px; }
  .chat-header { padding: 10px 10px; }
  .agent-meta { gap: 8px; }
  .agent-avatar { width: 32px; height: 32px; font-size: 16px; }
  .agent-name { font-size: 12px; }
  .agent-status { font-size: 9px; }
  .qa-btn { font-size: 10px; padding: 5px 7px; }
  
  .chat-box { padding: 12px 8px 90px; }
  .msg-bubble { max-width: 78%; font-size: 12px; padding: 9px 11px; }
  
  .action-bar { padding: 18px 8px 10px; gap: 5px; }
  .action-btn { font-size: 12px; padding: 8px 12px; min-height: 40px; }
  
  .sidebar-header { padding: 14px 12px; }
  .sidebar-header h3 { font-size: 15px; }
  .close-btn { width: 30px; height: 30px; font-size: 16px; }
  .sidebar-tools { padding: 10px; }
  .search-input { padding: 10px; font-size: 16px; }
  .chips { gap: 5px; }
  .chip-btn { padding: 6px 10px; font-size: 11px; }
  .faq-item { padding: 10px; }
  .faq-item summary { font-size: 12px; padding: 4px 0; }
  .faq-item .arrow { font-size: 10px; }
  .faq-item p { font-size: 11px; line-height: 1.6; }
}

/* 横屏移动端优化 */
@media (max-width: 768px) and (orientation: landscape) {
  .chat-panel { height: 90vh; min-height: 300px; }
  .intro-panel { padding: 20px 16px; }
}
</style>
